import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
  AxiosResponse,
} from "axios";
import { keycloak, getToken } from "./keycloak";
import { handleAxiosError, ErrorResponse } from "../errors/errorHandler";
import { pushGlobalSuccess } from "../success/globalSuccess";
import { pushGlobalForbidden } from "../permissions/globalForbidden";

const MIN_VALIDITY = 30;

/**
 * A request with `responseType: "blob"` gets its ERROR body as a Blob too, so
 * `handleAxiosError` cannot read `data.message` and degrades to a bare
 * "Error 400: Bad Request". Parse the JSON body back onto the response so the
 * backend's own message reaches the user — e.g. the WEB_IT export naming the
 * product rows that overflow DocGen's row limit.
 *
 * Mutates `error.response.data` in place; a non-JSON body (proxy HTML page,
 * empty body) is left untouched so the generic message still applies.
 */
async function rehydrateBlobErrorBody(
  error: AxiosError<ErrorResponse>
): Promise<void> {
  const body = error.response?.data as unknown;
  if (!error.response || !(body instanceof Blob)) return;

  try {
    error.response.data = JSON.parse(await body.text());
  } catch {
    // Body was not JSON — keep it as-is and fall back to the generic message.
  }
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      await keycloak.updateToken(MIN_VALIDITY);
    } catch {
      keycloak.login();
      throw new axios.Cancel(
        "Token expired and SSO ended, redirecting to login"
      );
    }
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const msg = response.config.meta?.successMsg;
    if (msg) pushGlobalSuccess(msg);
    return response;
  },
  async (error: AxiosError<ErrorResponse>) => {
    // The caller aborted the request itself (a newer filter replaced it): nothing failed.
    if (error.code === AxiosError.ERR_CANCELED && error.config?.signal?.aborted) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      try {
        await keycloak.updateToken(MIN_VALIDITY);
        const token = getToken();
        if (token && error.config) {
          const config = error.config as InternalAxiosRequestConfig & {
            _retried?: boolean;
          };
          if (config._retried) {
            // Honor the per-request opt-out (e.g. blob/PDF downloads that own
            // their localized message) on the retry-exhausted path too, so the
            // suppression contract holds at both interceptor exits.
            if (!config.meta?.suppressErrorToast) handleAxiosError(error);
            return Promise.reject(error);
          }
          config._retried = true;

          if (config.headers) {
            (config.headers as AxiosHeaders).set(
              "Authorization",
              `Bearer ${token}`
            );
          }
          return axiosInstance.request(config);
        }
      } catch {
        keycloak.login();
      }
    }

    // Permission denied for this feature/page: replace the page content with
    // the Permission Denied screen instead of leaving a stuck skeleton, and
    // skip the generic toast to avoid a duplicate error message.
    // Action-level 403s (meta.suppressForbiddenRedirect) opt out and fall
    // through to the normal error toast instead of replacing the page.
    if (
      error.response?.status === 403 &&
      !error.config?.meta?.suppressForbiddenRedirect
    ) {
      pushGlobalForbidden();
      return Promise.reject(error);
    }

    // Caller opts out of the generic error toast to handle the failure itself
    // (e.g. blob/PDF downloads that surface their own localized message).
    if (error.config?.meta?.suppressErrorToast) {
      return Promise.reject(error);
    }

    await rehydrateBlobErrorBody(error);
    handleAxiosError(error);
    return Promise.reject(error);
  }
);

export default axiosInstance;
